<template>
  <div class="form-rich-select-wrap">
    <v-select
      ref="selectRef"
      :options="displayOptions"
      v-model="selectedOption"
      @search="onToggleSearch"
      @open="onOpen"
      @close="onClose"
      @mouseenter="onHover"
      :get-option-label="getOptionLabel"
      :get-option-key="getOptionKey"
      :placeholder="placeholder"
      :class="['w-full', 'form-rich-select', { 'error': error, 'form-rich-select--card': isCardLayout, 'form-rich-select--empty': isCardLayout && !hasCardSelection }]"
      :calculate-position="calculatePosition"
      v-bind="mergedAttrs"
    >
      <template #list-header>
        <li
          v-if="isCardLayout"
          class="form-rich-select-panel-search-wrap"
          role="presentation"
          @mousedown.stop="keepOpenForPanelSearch"
        >
          <div class="form-rich-select-panel-search">
            <Search class="form-rich-select-panel-search-icon" :size="18" />
            <input
              ref="panelSearchRef"
              v-model="panelSearch"
              type="search"
              class="form-rich-select-panel-search-input"
              :placeholder="placeholder"
              autocomplete="off"
              @input="onPanelSearchInput"
              @keydown="onPanelSearchKeydown"
            />
            <div v-if="panelLoading" class="form-rich-select-panel-search-spinner" aria-hidden="true" />
          </div>
        </li>
      </template>
      <template #no-options>
        <div class="form-rich-select-no-options">
          <span>{{ noOptionsMessage }}</span>
          <button
            v-if="noOptionsActionVisible && noOptionsActionLabel"
            type="button"
            class="form-rich-select-no-options-action"
            @click.stop.prevent="onNoOptionsActionClick"
          >
            {{ noOptionsActionLabel }}
          </button>
        </div>
      </template>
      <template #spinner="{ loading }">
        <div v-if="loading && !isCardLayout" class="vs__spinner">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        </div>
      </template>
      <template #option="option">
        <div v-if="isCardLayout" class="form-rich-select-option-card">
          <div class="form-rich-select-option-card-title">
            <template v-for="(part, index) in highlightParts(optionTitle(option))" :key="'t-' + index">
              <mark v-if="part.mark" class="form-rich-select-highlight">{{ part.text }}</mark>
              <span v-else>{{ part.text }}</span>
            </template>
          </div>
          <div v-if="optionMeta(option)" class="form-rich-select-option-card-meta">
            <template v-for="(part, index) in highlightParts(optionMeta(option))" :key="'m-' + index">
              <mark v-if="part.mark" class="form-rich-select-highlight">{{ part.text }}</mark>
              <span v-else>{{ part.text }}</span>
            </template>
          </div>
          <div v-if="optionDescription(option)" class="form-rich-select-option-card-description">
            <template v-for="(part, index) in highlightParts(optionDescription(option))" :key="'d-' + index">
              <mark v-if="part.mark" class="form-rich-select-highlight">{{ part.text }}</mark>
              <span v-else>{{ part.text }}</span>
            </template>
          </div>
        </div>
        <span
          v-else
          :class="getOptionClass(option)"
          @mouseenter="setNativeTitleIfTruncated"
        >
          {{ getOptionLabel(option) }}
        </span>
      </template>
      <template #selected-option="option">
        <span v-if="isCardLayout" class="form-rich-select-selected">
          <span class="form-rich-select-selected-title" @mouseenter="setNativeTitleIfTruncated">{{ optionTitle(option) }}</span>
          <span
            v-if="optionMeta(option)"
            class="form-rich-select-selected-meta"
            @mouseenter="setNativeTitleIfTruncated"
          >{{ optionMeta(option) }}</span>
        </span>
        <span
          v-else
          :class="applySelectedOptionClass ? getOptionClass(option) : ''"
          @mouseenter="setNativeTitleIfTruncated"
        >
          {{ getOptionLabel(option) }}
        </span>
      </template>
      <template #list-footer>
        <li
          v-if="resultMeta"
          class="form-rich-select-footer-wrap"
          role="presentation"
          @mousedown.stop="keepOpenForPanelSearch"
        >
          <div class="form-rich-select-footer">
            {{ short ? 'totaal' : 'Totaal aantal opties' }} <strong>{{ formatCount(resultMeta.total) }}</strong>
            <template v-if="hasActiveFilter">
              <span class="form-rich-select-footer-sep">|</span>
              {{ short ? 'resultaten' : 'Resultaten' }} <strong>{{ formatCount(resultMeta.filtered) }}</strong>
            </template>
          </div>
        </li>
      </template>
    </v-select>
    <div v-if="error" class="form-error">
      <svg fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      {{ error }}
    </div>
  </div>
</template>

<script setup>
defineOptions({
  name: 'FormRichSelect',
  inheritAttrs: false,
});

import { ref, watch, computed, nextTick, useAttrs } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { Search } from '@lucide/vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import axios from 'axios';

const props = defineProps({
  modelValue: {
    type: [String, Number, Object, Array, Boolean],
    default: null
  },
  initialOptions: {
    type: [Object, Array],
    default: null
  },
  searchUrl: {
    type: String,
    required: false,
    default: null
  },
  minSearchChars: {
    type: Number,
    default: 1
  },
  // Note: If searchUrl is not provided, the component automatically switches to local search mode
  // using the initialOptions data with built-in v-select filtering
  error: {
    type: String,
    default: ''
  },
  optionLabel: {
    type: String,
    default: 'label'
  },
  optionValue: {
    type: String,
    default: 'value'
  },
  // Common v-select props
  placeholder: {
    type: String,
    default: 'Selecteer...'
  },
  multiple: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  searchable: {
    type: Boolean,
    default: true
  },
  closeOnSelect: {
    type: Boolean,
    default: true
  },
  selectOnTab: {
    type: Boolean,
    default: false
  },
  appendToBody: {
    type: Boolean,
    default: true
  },
  deselectFromDropdown: {
    type: Boolean,
    default: false
  },
  /**
   * When true (multiple mode), already-selected options are omitted from the
   * dropdown list so they cannot be added again. Selected tags remain visible
   * on the control. Matches legacy @vueform/multiselect hide-selected.
   */
  hideSelected: {
    type: Boolean,
    default: false
  },
  taggable: {
    type: Boolean,
    default: false
  },
  tabindex: {
    type: [String, Number],
    default: null
  },
  returnObject: {
    type: Boolean,
    default: false
  },
  optionClassField: {
    type: String,
    default: null
  },
  /**
   * When false, the plain-layout `#selected-option` renders the label without
   * `getOptionClass`. Used by the FormSelect compat wrapper to preserve the
   * legacy look where a deprecated option shows normal text when the control
   * is closed (strikethrough only appears in the open dropdown).
   */
  applySelectedOptionClass: {
    type: Boolean,
    default: true
  },
  // Compact footer counters for the smaller (filter) variant: "totaal"/"filtered"
  // instead of the full "Totaal aantal opties"/"Aantal na filtering" labels.
  short: {
    type: Boolean,
    default: false
  },
  labelId: {
    type: String,
    default: null
  },
  disableHoverPrefetch: {
    type: Boolean,
    default: false
  },
  debounceMs: {
    type: Number,
    default: 300
  },
  noOptionsActionLabel: {
    type: String,
    default: ''
  },
  noOptionsActionVisible: {
    type: Boolean,
    default: false
  },
  /**
   * `card` = Variant A (panel search, two-line trigger, option cards).
   * `plain` keeps the existing vue-select row layout so other call sites
   * are unchanged. Footer still appears in either layout when the endpoint
   * returns `meta`.
   */
  optionLayout: {
    type: String,
    default: 'plain',
    validator: (value) => ['plain', 'card'].includes(value),
  },
});


const emit = defineEmits(['update:modelValue', 'selected', 'search', 'no-options-action']);

// Computed property to determine if we're in local mode (no searchUrl)
const isLocalMode = computed(() => !props.searchUrl);

const isCardLayout = computed(() => props.optionLayout === 'card');

// Tracks the current remote search term so the #no-options slot can distinguish
// between "nothing typed yet" and "typed but no results".
const currentSearchTerm = ref('');

/*
 * No-options message for the #no-options slot.
 *
 * In local mode there is nothing to type (options come from initialOptions), so
 * we simply state that no options are available.
 *
 * In server (search-url) mode the message must reflect the search state,
 * otherwise the dropdown keeps saying "Typ om te zoeken..." even after the user
 * has typed a query that returned zero results:
 *   - no search term yet  -> prompt to type ("Typ om te zoeken...")
 *   - searched, no results -> "Geen resultaten gevonden"
 */
const noOptionsMessage = computed(() => {
  if (isLocalMode.value) {
    return 'Geen opties beschikbaar';
  }

  return currentSearchTerm.value.length > 0
    ? 'Geen resultaten gevonden'
    : 'Typ om te zoeken...';
});

const isFilled = (value) => value !== undefined && value !== null;

const getOptionLabel = computed(() => {
  return (option) => {
    if (!option) return '';
    if (typeof option === 'string' || typeof option === 'number') {
      return String(option);
    }
    if (props.optionLabel && isFilled(option[props.optionLabel])) {
      return option[props.optionLabel];
    }
    return option.label || option.title || option.name || '';
  };
});

const getOptionKey = computed(() => {
  return (option) => {
    if (!option) return '';
    if (typeof option === 'string' || typeof option === 'number') {
      return option;
    }
    if (props.optionValue && isFilled(option[props.optionValue])) {
      return option[props.optionValue];
    }
    return option.value ?? option.id ?? '';
  };
});

// Method to get CSS class for an option
/**
 * Native browser tooltip (`title`) only when the label is actually clipped.
 * Short labels stay without a tooltip.
 */
const setNativeTitleIfTruncated = (event) => {
  const el = event.currentTarget;
  if (!(el instanceof HTMLElement)) {
    return;
  }

  const full = (el.textContent || '').trim();
  el.title = full && el.scrollWidth > el.clientWidth ? full : '';
};

const getOptionClass = (option) => {
  if (!option) return '';

  // If optionClassField is specified, use the value from that field
  if (props.optionClassField && option[props.optionClassField]) {
    return option[props.optionClassField];
  }

  // Fallback to checking for a 'class' property
  return option.class || '';
};

const optionTitle = (option) => {
  if (!option) return '';
  return option.title || getOptionLabel.value(option);
};

const optionMeta = (option) => {
  if (!option) return '';
  return option.meta || '';
};

const optionDescription = (option) => {
  if (!option) return '';
  return option.description || '';
};

/**
 * Split `text` so the current search term can be wrapped in <mark>.
 * Comparison is case-insensitive; original casing is preserved.
 */
const highlightParts = (text) => {
  const source = text == null ? '' : String(text);
  const term = currentSearchTerm.value;
  if (!source || !term) {
    return [{ text: source, mark: false }];
  }

  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const splitter = new RegExp(`(${escaped})`, 'gi');
  return source.split(splitter).filter((part) => part !== '').map((part) => ({
    text: part,
    mark: part.toLowerCase() === term.toLowerCase(),
  }));
};

const formatCount = (value) => Number(value || 0).toLocaleString('nl-NL');

const resultMeta = ref(null);

/**
 * Pull `meta` off a W14 envelope. Arrays and missing meta yield null so the
 * footer stays hidden.
 */
const extractIncomingMeta = (responseData) => {
  if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
    return null;
  }

  const meta = responseData.meta;
  if (!meta || typeof meta !== 'object') {
    return null;
  }

  return {
    total: Number(meta.total) || 0,
    filtered: Number(meta.filtered) || 0,
    returned: Number(meta.returned) || 0,
    limit: Number(meta.limit) || 0,
    offset: Number(meta.offset) || 0,
    hasMore: Boolean(meta.hasMore),
  };
};

/**
 * Accept a W14 envelope `{options, meta}`, a Laravel `{data: [...]}` wrapper,
 * a bare options array, or a single option object. Always `{ options, meta }`.
 */
const unpackDropdownPayload = (data) => {
  if (!data) {
    return { options: [], meta: null };
  }

  if (Array.isArray(data)) {
    return { options: data, meta: null };
  }

  if (typeof data !== 'object') {
    return { options: [], meta: null };
  }

  if (Array.isArray(data.options)) {
    return { options: data.options, meta: extractIncomingMeta(data) };
  }

  if (data.data && Array.isArray(data.data.options)) {
    return { options: data.data.options, meta: extractIncomingMeta(data.data) };
  }

  if (Array.isArray(data.data)) {
    return { options: data.data, meta: extractIncomingMeta(data) };
  }

  const looksLikeOption = isFilled(data.value) || isFilled(data.id) || isFilled(data.label);
  if (looksLikeOption) {
    return { options: [data], meta: null };
  }

  return { options: [], meta: null };
};

/**
 * Guarantee a `value` key so the default option-value="value" works when the
 * backend only sent `id`. Existing `id` is kept for option-value="id" call sites.
 */
const normalizeOption = (option) => {
  if (!option || typeof option !== 'object') {
    return option;
  }
  if (!isFilled(option.value) && isFilled(option.id)) {
    return { ...option, value: option.id };
  }
  return option;
};

const normalizeOptions = (list) => {
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map(normalizeOption);
};

const uniqueByOptionValue = (list) => {
  const seen = new Set();
  const unique = [];
  for (const opt of list) {
    if (!opt) {
      continue;
    }
    const key = typeof opt === 'object'
      ? (isFilled(opt[props.optionValue]) ? opt[props.optionValue] : (opt.value ?? opt.id))
      : opt;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(opt);
  }
  return unique;
};

const extractIncomingOptions = (responseData) => {
  return normalizeOptions(unpackDropdownPayload(responseData).options);
};

/**
 * The "Resultaten" counter is only meaningful when a filter actually narrowed
 * the set. When the endpoint reports `filtered === total` (no search applied,
 * or a search that matches everything) the second counter is redundant noise
 * and is hidden.
 */
const hasActiveFilter = computed(
  () => Boolean(resultMeta.value) && resultMeta.value.filtered !== resultMeta.value.total
);
const panelSearch = ref('');
const panelSearchRef = ref(null);
const panelLoading = ref(false);
const dropdownListEl = ref(null);
const loadedOffset = ref(0);
const loadingMore = ref(false);
let fetchGeneration = 0;

// Computed property to pass through v-select specific props
const bindPropsToInput = computed(() => {
  const vSelectProps = { ...props };
  delete vSelectProps.modelValue;
  delete vSelectProps.initialOptions;
  delete vSelectProps.searchUrl;
  delete vSelectProps.minSearchChars;
  delete vSelectProps.error;
  delete vSelectProps.optionLabel;
  delete vSelectProps.optionValue;
  delete vSelectProps.hideSelected;
  delete vSelectProps.noOptionsActionLabel;
  delete vSelectProps.noOptionsActionVisible;
  delete vSelectProps.disableHoverPrefetch;
  delete vSelectProps.debounceMs;
  delete vSelectProps.returnObject;
  delete vSelectProps.optionClassField;
  delete vSelectProps.labelId;
  delete vSelectProps.optionLayout;

  // In local mode, enable built-in filtering
  if (isLocalMode.value) {
    vSelectProps.filterable = true;
  } else {
    vSelectProps.filterable = false;
  }

  return vSelectProps;
});

// Merge incoming $attrs (like id, class) with computed v-select props
const attrs = useAttrs();
const mergedAttrs = computed(() => {
  const { id, ...otherAttrs } = attrs;
  return {
    ...bindPropsToInput.value,
    ...otherAttrs,
    // If an id is provided, pass it as input-id to v-select so the internal input gets the ID
    // This ensures label for attributes work correctly
    ...(id && { 'input-id': id }),
    // Also keep the id for the outer container for other potential uses
    ...(id && { id }),
    // Add aria-labelledby if labelId is provided
    ...(props.labelId && { 'aria-labelledby': props.labelId }),
    // Card layout uses the panel search, not vue-select's toggle input.
    // Keeping that input searchable makes a blur on the hidden field close
    // the menu as soon as we focus the panel (looks like a dead click).
    ...(isCardLayout.value ? { searchable: false } : {}),
    // Turn Tab into highlight navigation (Shift+Tab moves up) instead of
    // blurring the control. vue-select 4-beta never emits `search:keydown`
    // despite declaring it, so we use the `mapKeydown` prop which it calls
    // with its keymap and instance. Enter still selects via the default map.
    ...(typeof attrs.mapKeydown === 'undefined' && typeof attrs['map-keydown'] === 'undefined'
      ? {
          'map-keydown': (map, vm) => {
            map[9] = (e) => {
              if (!vm.dropdownOpen || !vm.filteredOptions || vm.filteredOptions.length === 0) {
                return;
              }
              e.preventDefault();
              e.shiftKey ? vm.typeAheadUp() : vm.typeAheadDown();
            };
            return map;
          },
        }
      : {}),
  };
});



const options = ref([]);

// Template ref for the underlying vue-select instance.
// Used to programmatically close the dropdown (e.g. after clicking the
// "add new" action so the modal can take over without the menu covering it).
const selectRef = ref(null);

/**
 * Options shown in the open dropdown. When hideSelected is on (multiple),
 * selected values are filtered out so they only appear as tags, not as
 * re-selectable rows.
 */
const matchesLocalCardSearch = (option) => {
  const term = currentSearchTerm.value.toLowerCase();
  if (!term) {
    return true;
  }

  const haystack = [
    optionTitle(option),
    optionMeta(option),
    optionDescription(option),
    getOptionLabel.value(option),
  ].join(' ').toLowerCase();

  return haystack.includes(term);
};

const displayOptions = computed(() => {
  const source = isCardLayout.value && isLocalMode.value && currentSearchTerm.value
    ? options.value.filter(matchesLocalCardSearch)
    : options.value;

  if (!props.hideSelected || !props.multiple) {
    return source;
  }

  const selectedValues = new Set();
  const selected = selectedOption.value;
  if (Array.isArray(selected)) {
    for (const opt of selected) {
      if (opt && typeof opt === 'object' && opt[props.optionValue] !== undefined) {
        selectedValues.add(opt[props.optionValue]);
      }
    }
  }
  if (Array.isArray(props.modelValue)) {
    for (const val of props.modelValue) {
      if (val !== null && val !== undefined && typeof val !== 'object') {
        selectedValues.add(val);
      }
    }
  }

  return source.filter(
    (opt) => opt && !selectedValues.has(opt[props.optionValue])
  );
});


const findOptionByValue = (availableOptions, targetValue) => {
  if (targetValue === null || targetValue === undefined) {
    return null;
  }

  const strictMatch = availableOptions.find((opt) => opt && opt[props.optionValue] === targetValue);
  if (strictMatch) {
    return strictMatch;
  }

  return availableOptions.find(
    (opt) => opt && String(opt[props.optionValue]) === String(targetValue)
  ) || null;
};

// Normalize incoming modelValue (IDs or objects) into option objects for v-select
const normalizeToOptionObjects = (modelValue, availableOptions, multiple) => {
  
  // Build a map of optionValue -> first occurrence of the option to avoid duplicates
  const valueToOption = new Map();
  for (const opt of availableOptions) {
    if (opt && typeof opt === 'object' && !valueToOption.has(opt[props.optionValue])) {
      valueToOption.set(opt[props.optionValue], opt);
    }
  }

  if (multiple) {
    if (Array.isArray(modelValue)) {
      // If already objects, return as-is
      if (modelValue.length > 0 && typeof modelValue[0] === 'object' && modelValue[0] !== null) {
        // Also deduplicate by optionValue while preserving current order
        const seen = new Set();
        const uniqueObjects = [];
        for (const obj of modelValue) {
          if (obj && !seen.has(obj[props.optionValue])) {
            seen.add(obj[props.optionValue]);
            uniqueObjects.push(obj);
          }
        }
        return uniqueObjects;
      }
      // Map values to option objects using the map, preserving modelValue order
      const result = [];
      for (const val of modelValue) {
        if (valueToOption.has(val)) {
          result.push(valueToOption.get(val));
          continue;
        }

        const fallbackMatch = findOptionByValue(availableOptions, val);
        if (fallbackMatch) {
          result.push(fallbackMatch);
        }
      }
      return result;
    }
    return [];
  } else {
    // Single select
    if (modelValue && typeof modelValue === 'object') {
      return modelValue;
    }
    if (modelValue === null || modelValue === undefined) {
      return null;
    }
    return valueToOption.get(modelValue) || findOptionByValue(availableOptions, modelValue);
  }
};

// Build initial option pool from initialOptions prop
const initialOptionPool = () => {
  const { options: raw } = unpackDropdownPayload(props.initialOptions);
  return uniqueByOptionValue(normalizeOptions(raw));
};


const selectedOption = ref(
  normalizeToOptionObjects(
    props.modelValue,
    initialOptionPool(),
    props.multiple
  )
);

const hasCardSelection = computed(() => {
  const selected = selectedOption.value;
  if (props.multiple) {
    return Array.isArray(selected) && selected.length > 0;
  }
  return selected !== null && selected !== undefined;
});

const isSyncingFromProps = ref(false);
const hasPrefetched = ref(false);
const hasFetchedFromServer = ref(false);

watch(() => props.initialOptions, (newOptions) => {
  const unpacked = unpackDropdownPayload(newOptions);
  const incomingOptions = uniqueByOptionValue(normalizeOptions(unpacked.options));

  if (!hasFetchedFromServer.value) {
    options.value = incomingOptions;
    resultMeta.value = unpacked.meta;
  }

  if (props.modelValue !== null && props.modelValue !== undefined) {
    const pool = [...initialOptionPool(), ...options.value];
    selectedOption.value = normalizeToOptionObjects(
      props.modelValue,
      pool,
      props.multiple
    );
  }
}, { immediate: true });

// Helpers to know if our current options list only contains the selected items
const getSelectedValues = () => {
  if (props.multiple) {
    return new Set((Array.isArray(selectedOption.value) ? selectedOption.value : [])
      .filter(o => o && typeof o === 'object')
      .map(o => o[props.optionValue]));
  }
  if (selectedOption.value && typeof selectedOption.value === 'object') {
    return new Set([selectedOption.value[props.optionValue]]);
  }
  return new Set();
};

const optionsContainOnlySelected = () => {
  if (options.value.length === 0) return true;
  const selectedValues = getSelectedValues();
  // If there is any option that is not selected, then we already have more than just selected
  return options.value.every(opt => selectedValues.has(opt[props.optionValue]));
};

const onHover = () => {
  // Only prefetch in server mode and if not disabled
  if (
    !props.disableHoverPrefetch
    && !isLocalMode.value
    && props.minSearchChars <= 1
    && !hasPrefetched.value
    && (options.value.length === 0 || optionsContainOnlySelected())
  ) {
    hasPrefetched.value = true;
    fetchOptions('', null, true);
  }
};

const reloadAllRemoteOptions = (loading) => {
  if (loading) {
    loading(true);
  }
  fetchOptions('', loading);
};

/**
 * vue-select closes on vs__search blur unless `mousedown` is true (its
 * "clicking inside the control" guard). The card panel search is a different
 * input, so focusing it would otherwise close the menu immediately.
 */
const keepOpenForPanelSearch = () => {
  if (selectRef.value) {
    selectRef.value.mousedown = true;
  }
  panelSearchRef.value?.focus();
};

const onOpen = () => {
  currentSearchTerm.value = '';
  hasPrefetched.value = false;
  panelSearch.value = '';

  if (!isLocalMode.value) {
    reloadAllRemoteOptions(null);
  }

  if (isCardLayout.value) {
    nextTick(() => {
      keepOpenForPanelSearch();
    });
  }
};

const onClose = () => {
  currentSearchTerm.value = '';
  panelSearch.value = '';
  panelLoading.value = false;
  loadingMore.value = false;
};

const onToggleSearch = (search, loading) => {
  if (isCardLayout.value) {
    if (loading) loading(false);
    return;
  }
  onSearch(search, loading);
};

const onPanelSearchInput = () => {
  const loading = (isLoading) => {
    panelLoading.value = Boolean(isLoading);
  };
  onSearch(panelSearch.value, loading);
};

/**
 * Card-layout keyboard navigation. The panel search is our own `<input>`
 * (vue-select's search is `readonly` in card layout), so vue-select's
 * keydown handler never fires — drive Tab/Arrow/Enter manually through
 * vue-select's type-ahead methods. Tab moves the highlight (Shift+Tab up),
 * Enter selects the highlighted option.
 */
const onPanelSearchKeydown = (e) => {
  const vs = selectRef.value;
  if (!vs) return;
  const opts = vs.filteredOptions || [];
  if (opts.length === 0 || !vs.dropdownOpen) return;

  if (e.key === 'Tab') {
    e.preventDefault();
    e.shiftKey ? vs.typeAheadUp() : vs.typeAheadDown();
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    vs.typeAheadDown();
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    vs.typeAheadUp();
  } else if (e.key === 'Enter') {
    e.preventDefault();
    vs.typeAheadSelect();
  }
};

const onSearch = (search, loading) => {
  const normalizedSearch = typeof search === 'string' ? search.trim() : '';
  currentSearchTerm.value = normalizedSearch;
  emit('search', normalizedSearch);

  if (isLocalMode.value) {
    if (loading) loading(false);
    return;
  }

  if (normalizedSearch.length >= props.minSearchChars) {
    loading(true);
    debouncedFetchOptions(normalizedSearch, loading);
    return;
  }

  if (normalizedSearch.length === 0) {
    reloadAllRemoteOptions(loading);
    return;
  }

  if (loading) loading(false);
};

const onNoOptionsActionClick = () => {
  // Close the dropdown so the underlying modal/form can take focus.
  if (selectRef.value && typeof selectRef.value.closeSearchOptions === 'function') {
    selectRef.value.closeSearchOptions();
  }
  emit('no-options-action', currentSearchTerm.value);
};

const debouncedFetchOptions = useDebounceFn((search, loading) => {
  fetchOptions(search, loading);
}, props.debounceMs);

const maybeLoadMore = () => {
  const menu = dropdownListEl.value;
  if (!menu || loadingMore.value || isLocalMode.value || !resultMeta.value?.hasMore) {
    return;
  }

  if (menu.scrollTop + menu.clientHeight < menu.scrollHeight - 64) {
    return;
  }

  loadingMore.value = true;
  fetchOptions(currentSearchTerm.value, (isLoading) => {
    if (!isLoading) {
      loadingMore.value = false;
    }
  }, false, true);
};

const fetchOptions = (search, loading, isPrefetch = false, append = false) => {
  if (!append) {
    fetchGeneration += 1;
    loadedOffset.value = 0;
  }

  const generation = fetchGeneration;
  const params = {
    search,
    offset: append ? loadedOffset.value : 0,
  };

  axios.get(props.searchUrl, { params })
    .then(response => {
      if (generation !== fetchGeneration) {
        return;
      }

      hasFetchedFromServer.value = true;
      const incoming = extractIncomingOptions(response.data);
      const meta = extractIncomingMeta(response.data);
      resultMeta.value = meta;
      loadedOffset.value = (meta?.offset ?? params.offset) + incoming.length;

      if (isPrefetch) {
        // Merge only when our current options do not include any non-selected items
        if (optionsContainOnlySelected()) {
          const merged = [...options.value, ...incoming];
          const seen = new Set();
          options.value = merged.filter(opt => {
            if (!opt || seen.has(opt[props.optionValue])) return false;
            seen.add(opt[props.optionValue]);
            return true;
          });
        }
        return;
      }

      if (append) {
        const menu = dropdownListEl.value;
        const scrollTop = menu ? menu.scrollTop : 0;
        const seen = new Set(
          options.value
            .filter((opt) => opt && typeof opt === 'object')
            .map((opt) => opt[props.optionValue])
        );
        const extra = incoming.filter((opt) => opt && !seen.has(opt[props.optionValue]));
        options.value = [...options.value, ...extra];
        nextTick(() => {
          if (menu) {
            menu.scrollTop = scrollTop;
          }
          maybeLoadMore();
        });
        return;
      }

      // For search, replace options but ensure uniqueness and preserve selected options
      const seen = new Set();

      // First, add the currently selected option(s) to preserve them
      const selectedOptions = Array.isArray(selectedOption.value) ? selectedOption.value :
                            (selectedOption.value ? [selectedOption.value] : []);

      const finalOptions = [];

      // Add selected options first
      selectedOptions.forEach(opt => {
        if (opt && !seen.has(opt[props.optionValue])) {
          finalOptions.push(opt);
          seen.add(opt[props.optionValue]);
        }
      });

      // Then add incoming search results
      incoming.forEach(opt => {
        if (opt && !seen.has(opt[props.optionValue])) {
          finalOptions.push(opt);
          seen.add(opt[props.optionValue]);
        }
      });

      options.value = finalOptions;
      nextTick(() => maybeLoadMore());
    })
    .catch(error => {
      console.error("Error fetching options:", error);
    })
    .finally(() => {
      if (generation !== fetchGeneration && !append) {
        return;
      }
      if (loading) loading(false);
      if (!append) {
        panelLoading.value = false;
      }
      if (append) {
        loadingMore.value = false;
      }
    });
};

watch(selectedOption, (newValue) => {
  if (isSyncingFromProps.value) return;

  if (props.multiple) {
    const emittedValue = props.returnObject ? newValue : (newValue ? newValue.map(item => item[props.optionValue]) : []);
    // Skip emit if no change to avoid loops
    if (Array.isArray(props.modelValue)
      && emittedValue.length === props.modelValue.length
      && emittedValue.every((item, idx) => {
        if (props.returnObject) {
          return item[props.optionValue] === props.modelValue[idx][props.optionValue];
        }
        return item === props.modelValue[idx];
      })) {
      return;
    }
    emit('update:modelValue', emittedValue);
  } else {
    const emittedValue = props.returnObject ? newValue : (newValue ? newValue[props.optionValue] : null);
    if (props.modelValue === emittedValue) {
      return;
    }
    emit('update:modelValue', emittedValue);
  }
  emit('selected', newValue);
});

watch(() => props.modelValue, (newValue) => {
  const pool = [...initialOptionPool(), ...options.value];
  isSyncingFromProps.value = true;
  if (props.multiple) {
    if (Array.isArray(newValue)) {
      selectedOption.value = normalizeToOptionObjects(newValue, pool, true);
    } else if (newValue === null || newValue === undefined) {
      selectedOption.value = [];
    }
  } else {
    if (newValue === null || newValue === undefined) {
      selectedOption.value = null;
    } else {
      selectedOption.value = normalizeToOptionObjects(newValue, pool, false);
      // If normalizeToOptionObjects returns null, try to find a matching option in the options array
      if (!selectedOption.value && options.value.length > 0) {
        const matchingOption = options.value.find(opt => opt && String(opt[props.optionValue]) === String(newValue));
        if (matchingOption) {
          selectedOption.value = matchingOption;
        }
      }
    }
  }
  nextTick(() => { isSyncingFromProps.value = false; });
}, { immediate: true });

watch(() => options.value, (newOptions) => {
  if (props.multiple) return;

  const modelValue = props.modelValue;
  
  if (modelValue === null || modelValue === undefined || !Array.isArray(newOptions) || newOptions.length === 0) {
    return;
  }

  const currentSelected = selectedOption.value;
  const hasMatchingSelection = currentSelected
    && typeof currentSelected === 'object'
    && String(currentSelected[props.optionValue]) === String(modelValue);

  // Keep existing selection if it already matches the current model value.
  if (hasMatchingSelection) return;

  const pool = [...initialOptionPool(), ...newOptions];
  let rematchedOption = normalizeToOptionObjects(modelValue, pool, false);

  if (!rematchedOption) {
    rematchedOption = newOptions.find(
      (opt) => opt && String(opt[props.optionValue]) === String(modelValue)
    ) || null;
  }

  if (!rematchedOption) return;

  isSyncingFromProps.value = true;
  selectedOption.value = rematchedOption;
  nextTick(() => { isSyncingFromProps.value = false; });
}, { deep: true });

const calculatePosition = (dropdownList, component) => {
  dropdownListEl.value = dropdownList;
  dropdownList.addEventListener('scroll', maybeLoadMore);
  nextTick(() => maybeLoadMore());

  const place = () => {
    const toggle = component && component.$refs ? component.$refs.toggle : null;
    if (!toggle) {
      return;
    }

    const rect = toggle.getBoundingClientRect();
    const menuHeight = dropdownList.offsetHeight || 0;
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = menuHeight > 0
      ? spaceBelow < menuHeight && spaceAbove >= menuHeight
      : spaceBelow < spaceAbove;

    dropdownList.style.position = 'absolute';
    dropdownList.style.zIndex = 'var(--z-modal)';

    if (openUpward) {
      dropdownList.style.top = `${scrollY + rect.top - menuHeight}px`;
      dropdownList.classList.add('vs--drop-up');
    } else {
      dropdownList.style.top = `${scrollY + rect.bottom}px`;
      dropdownList.classList.remove('vs--drop-up');
    }

    dropdownList.style.left = `${scrollX + rect.left}px`;
    dropdownList.style.width = `${rect.width}px`;
    dropdownList.style.maxWidth = `${rect.width}px`;
    dropdownList.style.boxSizing = 'border-box';
    dropdownList.style.overflowX = 'hidden';

    if (isCardLayout.value) {
      dropdownList.classList.add('form-rich-select-menu--card');
    } else {
      dropdownList.classList.remove('form-rich-select-menu--card');
    }
  };

  place();

  let rafId = null;
  const onScrollOrResize = () => {
    if (rafId !== null) {
      return;
    }
    rafId = requestAnimationFrame(() => {
      rafId = null;
      place();
    });
  };

  window.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);

  const resizeObserver = new ResizeObserver(() => place());
  resizeObserver.observe(dropdownList);

  return () => {
    dropdownList.removeEventListener('scroll', maybeLoadMore);
    if (dropdownListEl.value === dropdownList) {
      dropdownListEl.value = null;
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
    window.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
    resizeObserver.disconnect();
    dropdownList.classList.remove('vs--drop-up');
    dropdownList.classList.remove('form-rich-select-menu--card');
  };
};

</script>

